import { useCallback, useEffect, useRef, useState, ChangeEvent } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import getByPath from 'lodash/get'
import { useDispatch } from 'react-redux'
import EditorTitle from 'builder/components/EditorTitle'
import { TextField } from 'builder/components/TextField'
import LanguageSelect from 'builder/components/LanguageSelect'
import EditorSection from 'builder/components/EditorSection'
import EditorRow from 'builder/components/EditorRow'
import EditorField from 'builder/components/EditorField'
import RichTextArea from 'builder/components/RichTextArea'
import starWhite from 'builder/components/GenerateAICVBanner/StarWhite.svg'
import { CoverLetterRegenerationStatus } from 'builder/modules/constants'

import Paywall from 'builder/components/AiFeatures/Paywall'
import { EditorPanelType, GeneralEditorStore } from 'builder/modules/generalEditor'
import { CoverLetterEditorStore, actions } from 'builder/modules/coverLetterEditor'
import { useConfig } from 'builder/hooks/useConfig'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useI18n } from 'builder/hooks/useI18n'
import { useScrollState } from 'builder/hooks/useScrollState'
import loaderImage from 'images/builder/resume-editor/editor-loader.svg'
import { GenerateButton, Star } from '../GenerateAICVBanner/styles'
import { ButtonSize } from '../Button'
import { EditorPanelSwitcher, EditorPanelSwitcherItem } from '../EditorPanelSwitcher'
import { DownloadButton, EditorMobileFooter } from '../PreviewPanel'
import {
  EditorPanel,
  EditorSubtitle,
  LoaderContainer,
  Loader,
  Note,
  NoteIcon,
  NoteText,
  UserNavigation,
  RegenerateSection,
  RemainingAttemptsText,
  EditorTitleWrapper,
  ResumeHeaderBottomSpace,
  EditorContentCoverLetter,
  EditorScrollCoverLetter,
  EditorPanelCoverLetter,
  EditorPanelSwitcherWrapper,
} from './styles'
import { ErrorState } from './AICoverLetter/ErrorState'
import ConfirmationModal from './AICoverLetter/ConfirmationModal'
import { ResumeSideMenu } from './ResumeSideMenu/ResumeSideMenu'
import { BuilderType } from './ResumeSideMenu/types'

type Props = {
  editorState: GeneralEditorStore
  isOnline: boolean
  coverLetterState: CoverLetterEditorStore
  updateSimpleField: (fieldName: any, value: any, debounce?: boolean) => void
  isAPremiumUser: boolean
  aiClFreeAttempts: number
  setShowAICoverLetterPaywall: () => void
  fallbackLocale: string
  setShowConfirmRegenerateModal: (value: boolean) => void
  coverLetterId: number
  resetAIRegenerationStatus: () => void
  isContentAnimationPending: boolean
  mobilePanelSwitcherItems: EditorPanelSwitcherItem<EditorPanelType>[]
  handleDownloadDocument: () => void
  handleCombineDocuments: () => void
}

export const CoverLetterEditor = (props: Props) => {
  const {
    editorState,
    isOnline,
    aiClFreeAttempts,
    coverLetterId,
    coverLetterState,
    fallbackLocale,
    isAPremiumUser,
    resetAIRegenerationStatus,
    isContentAnimationPending,
    setShowAICoverLetterPaywall,
    setShowConfirmRegenerateModal,
    updateSimpleField,
    isContentAnimationPending: isContentAnimationInProgress,
    mobilePanelSwitcherItems,
    handleDownloadDocument,
    handleCombineDocuments,
  } = props
  const letterDetailsRef = useRef<HTMLDivElement>(null)
  const [shouldScrollToDetails, setShouldScrollToDetails] = useState(false)
  const config = useConfig()
  const dispatch = useDispatch()
  const mediaQueries = useMediaQueries()
  const { i18n: I18n } = useI18n()
  const coverLetterContent = coverLetterState.coverLetter?.content || ''

  const handleCoverLetterNameChange = useCallback(
    (value: string) => {
      updateSimpleField('name', value, true)
    },
    [updateSimpleField],
  )

  const handleLocaleSelect = useCallback(
    (id: string) => {
      updateSimpleField('locale', id, true)
    },
    [updateSimpleField],
  )

  const handleUpdateSimpleField = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      updateSimpleField(e.target.name, e.target.value, true)
    },
    [updateSimpleField],
  )

  const updateDescription = useCallback(
    (val: string) => {
      updateSimpleField('content', val, true)
    },
    [updateSimpleField],
  )

  const handleAnimationEnd = useCallback(() => {
    updateSimpleField('openCL', false)
  }, [updateSimpleField])

  useEffect(() => {
    if (isContentAnimationPending && coverLetterContent) {
      // We should scroll to details if animation is started
      setShouldScrollToDetails(true)
    }
  }, [isContentAnimationPending, coverLetterContent])

  useEffect(() => {
    if (shouldScrollToDetails && letterDetailsRef.current) {
      const timeout = setTimeout(() => {
        // Trigger the animation to scroll down to letter content
        letterDetailsRef.current?.scrollIntoView({ behavior: 'smooth' })
        setShouldScrollToDetails(false)
      }, 100)
      return () => clearTimeout(timeout)
    }
  }, [shouldScrollToDetails])

  const {
    coverLetter,
    aiGenerationSection: {
      showPaywall: showAICoverLetterPaywall,
      showConfirmationModal: showConfirmRegenerateModal,
      regenerationStatus,
    },
    editorPanelType,
  } = coverLetterState
  const { coverLetterTemplates, locales } = editorState
  const guideLink = getByPath(config, 'links.howToWriteCoverLetter')
  const { superApp, international } = config?.features || {}
  const coverLetterGenerationLimitReached = !isAPremiumUser && aiClFreeAttempts <= 0
  const { canScrollBottom } = useScrollState()

  const generateMethod = coverLetterGenerationLimitReached
    ? setShowAICoverLetterPaywall
    : () => setShowConfirmRegenerateModal(true)

  const handleClickGenerateButton = () => {
    trackInternalEvent('click_regenerate_cover_letter')
    generateMethod?.()
  }

  if (!coverLetter || coverLetterTemplates.length === 0) {
    return (
      <EditorPanel>
        <LoaderContainer>
          <Loader src={loaderImage} />
        </LoaderContainer>
      </EditorPanel>
    )
  }

  const isGeneratingCoverLetter =
    isContentAnimationInProgress || regenerationStatus === CoverLetterRegenerationStatus.generating

  const showErrorStateModal = regenerationStatus === CoverLetterRegenerationStatus.failed
  const errorStateButtonSize = !mediaQueries.isPhone ? ButtonSize.default : ButtonSize.small

  const onRetryErrorStateModal = () => {
    resetAIRegenerationStatus()
    setShowConfirmRegenerateModal(true)
  }

  const additionalContentStyle =
    coverLetter.aiGenerated && mediaQueries.isPhone ? { paddingBottom: '100px' } : {}

  const handleEditorPanelTypeChange = (type: EditorPanelType) => {
    dispatch(actions.setEditorPanelType(type))
  }

  return (
    <>
      <EditorPanelCoverLetter data-testid="editor-panel">
        {(superApp || international) && (
          <ResumeSideMenu isOnline={isOnline} builderType={BuilderType.coverLetterBuilder} />
        )}
        {!(superApp || international) && isOnline && <UserNavigation hasFeatures />}
        <EditorScrollCoverLetter>
          <EditorContentCoverLetter style={additionalContentStyle}>
            {!mediaQueries.isAboveTablet && (
              <EditorPanelSwitcherWrapper>
                <EditorPanelSwitcher
                  activePanelType={editorPanelType}
                  handlePanelTypeChange={handleEditorPanelTypeChange}
                  items={mobilePanelSwitcherItems}
                />
              </EditorPanelSwitcherWrapper>
            )}
            <EditorTitleWrapper>
              <EditorTitle
                onChange={handleCoverLetterNameChange}
                value={
                  coverLetter.name ? coverLetter.name : I18n.t('builder.resume_editor.untitled')
                }
              />
              <EditorSubtitle>
                <LanguageSelect
                  selected={coverLetter.locale || fallbackLocale}
                  onSelect={handleLocaleSelect}
                  options={locales}
                />
              </EditorSubtitle>
            </EditorTitleWrapper>

            <ResumeHeaderBottomSpace />

            <EditorSection title={I18n.t('builder.cover_letter_editor.personal_details')}>
              <EditorRow>
                <EditorField>
                  <TextField
                    label={I18n.t('builder.cover_letter_editor.full_name')}
                    value={coverLetter.senderName || ''}
                    name="senderName"
                    onChange={handleUpdateSimpleField}
                  />
                </EditorField>

                <EditorField>
                  <TextField
                    label={I18n.t('builder.cover_letter_editor.job_title')}
                    value={coverLetter.applyingPosition || ''}
                    name="applyingPosition"
                    onChange={handleUpdateSimpleField}
                    hint={I18n.t('builder.resume_editor.job_title_hint')}
                  />
                </EditorField>
              </EditorRow>

              <EditorRow>
                <EditorField>
                  <TextField
                    label={I18n.t('builder.cover_letter_editor.address')}
                    value={coverLetter.address || ''}
                    name="address"
                    onChange={handleUpdateSimpleField}
                  />
                </EditorField>

                <EditorField>
                  <TextField
                    label={I18n.t('builder.cover_letter_editor.email')}
                    value={coverLetter.email || ''}
                    name="email"
                    onChange={handleUpdateSimpleField}
                  />
                </EditorField>
              </EditorRow>

              <EditorRow>
                <EditorField>
                  <TextField
                    label={I18n.t('builder.cover_letter_editor.phone_number')}
                    value={coverLetter.phoneNumber || ''}
                    name="phoneNumber"
                    onChange={handleUpdateSimpleField}
                  />
                </EditorField>
              </EditorRow>
            </EditorSection>

            <EditorSection title={I18n.t('builder.cover_letter_editor.recipient_details')}>
              <EditorRow>
                <EditorField>
                  <TextField
                    label={I18n.t('builder.cover_letter_editor.company')}
                    value={coverLetter.company || ''}
                    name="company"
                    onChange={handleUpdateSimpleField}
                  />
                </EditorField>

                <EditorField>
                  <TextField
                    label={I18n.t('builder.cover_letter_editor.recipient_name')}
                    value={coverLetter.companyRecipient || ''}
                    name="companyRecipient"
                    onChange={handleUpdateSimpleField}
                  />
                </EditorField>
              </EditorRow>
            </EditorSection>

            <EditorSection
              title={I18n.t('builder.cover_letter_editor.letter_content')}
              containerRef={letterDetailsRef}
              noBorderRadiusOnBottom={mediaQueries.isAboveTablet}
            >
              <EditorRow>
                <RichTextArea
                  label={I18n.t('builder.cover_letter_editor.letter_content_label')}
                  value={coverLetter.content || ''}
                  isAnimating={isContentAnimationInProgress}
                  onAnimationEnd={handleAnimationEnd}
                  onChange={updateDescription}
                  height={180}
                  locale={coverLetter.locale}
                />
              </EditorRow>
              {guideLink && (
                <Note href={guideLink} target="_blank" rel="noreferrer noopener">
                  <NoteIcon />
                  <NoteText>{I18n.t('builder.cover_letter_editor.letter_content_note')}</NoteText>
                </Note>
              )}
              {coverLetter.aiGenerated && (
                <RegenerateSection>
                  <GenerateButton
                    disabled={isContentAnimationInProgress}
                    onClick={handleClickGenerateButton}
                  >
                    <Star
                      $showAnimation={isContentAnimationInProgress}
                      src={starWhite}
                      alt="Star Image"
                    />
                    {I18n.t(
                      isContentAnimationInProgress
                        ? 'builder.ai_cover_letter.generating'
                        : 'builder.ai_cover_letter.regenerate',
                    )}
                  </GenerateButton>
                  {!isAPremiumUser && (
                    <RemainingAttemptsText>{`${
                      aiClFreeAttempts > 0 ? aiClFreeAttempts : 'No'
                    } free attempt${
                      aiClFreeAttempts === 0 || aiClFreeAttempts > 1 ? 's' : ''
                    } left`}</RemainingAttemptsText>
                  )}
                </RegenerateSection>
              )}
            </EditorSection>

            {!mediaQueries.isAboveTablet && (
              <EditorMobileFooter isFooterShadowVisible={canScrollBottom}>
                <DownloadButton
                  handleDownloadDocument={handleDownloadDocument}
                  handleCombineDocuments={handleCombineDocuments}
                  shouldShowIcon={false}
                />
              </EditorMobileFooter>
            )}
          </EditorContentCoverLetter>
        </EditorScrollCoverLetter>

        {showAICoverLetterPaywall && <Paywall onClose={setShowAICoverLetterPaywall} />}
        {showConfirmRegenerateModal && (
          <ConfirmationModal {...{ coverLetterId, isGeneratingCoverLetter }} />
        )}
        {showErrorStateModal && (
          <ErrorState
            onClose={resetAIRegenerationStatus}
            onTryAgain={onRetryErrorStateModal}
            ButtonSize={errorStateButtonSize}
          />
        )}
      </EditorPanelCoverLetter>
    </>
  )
}
